import type { AnalysisResult, Medicine } from '../types';

const ANALYSIS_PREFIX = 'medcheck_analysis_';
const LATEST_ANALYSIS_KEY = 'medcheck_latest_analysis_id';
const DRAFT_PREFIX = 'medcheck_review_draft_';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface StoredValue<T> {
  timestamp: number;
  value: T;
}

export interface ReviewDraft {
  imageHash?: string;
  ocrText?: string;
  medicines: Medicine[];
}

function readStoredValue<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredValue<T>;
    if (!stored?.timestamp || Date.now() - stored.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return stored.value;
  } catch {
    return null;
  }
}

function writeStoredValue<T>(key: string, value: T) {
  localStorage.setItem(key, JSON.stringify({ timestamp: Date.now(), value }));
}

export function getAnalysisCacheKey(imageHash: string) {
  return `${ANALYSIS_PREFIX}${imageHash}`;
}

export function saveAnalysisResult(imageHash: string, analysis: AnalysisResult): string {
  writeStoredValue(getAnalysisCacheKey(imageHash), analysis);
  localStorage.setItem(LATEST_ANALYSIS_KEY, imageHash);
  return imageHash;
}

export function loadAnalysisResult(imageHash?: string): AnalysisResult | null {
  const resolvedHash = imageHash || localStorage.getItem(LATEST_ANALYSIS_KEY) || '';
  if (!resolvedHash) return null;
  return readStoredValue<AnalysisResult>(getAnalysisCacheKey(resolvedHash));
}

export function saveReviewDraft(imageHash: string, draft: ReviewDraft): string {
  writeStoredValue(`${DRAFT_PREFIX}${imageHash}`, draft);
  return imageHash;
}

export function loadReviewDraft(imageHash?: string): ReviewDraft | null {
  if (!imageHash) return null;
  return readStoredValue<ReviewDraft>(`${DRAFT_PREFIX}${imageHash}`);
}
